import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FeedbackContext = createContext(null);

export function FeedbackProvider({ children }) {
  const [submitted, setSubmitted] = useState(new Set());
  const [dismissed, setDismissed] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/feedback");
      setSubmitted(new Set(data.submitted_contexts || []));
    } catch (e) { console.error("feedback refresh failed", e); /* not authed / offline — keep empty */ }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && localStorage.getItem("dd_token")) refresh();
    else setLoaded(true);
  }, [refresh]);

  const shouldShow = useCallback(
    (ctx) => loaded && !submitted.has(ctx) && !dismissed.has(ctx),
    [loaded, submitted, dismissed]);
  const dismiss = useCallback((ctx) => setDismissed((s) => new Set(s).add(ctx)), []);
  const submit = useCallback(async (payload) => {
    const { data } = await api.post("/feedback", payload);
    setSubmitted((s) => new Set(s).add(payload.context));
    return data;
  }, []);

  return (
    <FeedbackContext.Provider value={{ shouldShow, dismiss, submit }}>
      {children}
    </FeedbackContext.Provider>
  );
}

export const useFeedback = () => useContext(FeedbackContext) || {
  shouldShow: () => false, dismiss: () => {}, submit: async () => {},
};

function Stars({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="feedback-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`feedback-star-${n}`}
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)} className="p-0.5">
          <Star size={26} className={(hover || value) >= n ? "fill-brand text-brand" : "text-[#CBD5E1]"} />
        </button>
      ))}
    </div>
  );
}

const HEADINGS = {
  comparison_completed: "How was the AI comparison?",
  letter_generated: "How was the dispute letter?",
  baseline_signed: "How was the baseline flow?",
  general: "Send us feedback",
};

function FeedbackCard({ context, onDone, onDismiss }) {
  const { submit } = useFeedback();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [feature, setFeature] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const send = async () => {
    if (!rating) return;
    setSaving(true);
    try {
      await submit({ context, rating, comment: comment.trim() || null, allow_featuring: feature });
      setDone(true);
      setTimeout(() => onDone && onDone(), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit feedback");
    } finally { setSaving(false); }
  };

  if (done) return (
    <div className="flex items-center gap-2 py-2 text-[#177245]" data-testid="feedback-thanks">
      <CheckCircle2 size={18} /> <span className="text-sm font-medium">Thanks for the feedback!</span>
    </div>
  );

  return (
    <div data-testid={`feedback-card-${context}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-navy">{HEADINGS[context] || "How was this?"}</p>
        {onDismiss && (
          <button data-testid="feedback-dismiss" onClick={onDismiss} className="text-[#94A3B8] hover:text-[#475569]"><X size={16} /></button>
        )}
      </div>
      <div className="mt-2"><Stars value={rating} onChange={setRating} /></div>
      {rating > 0 && (
        <div className="mt-3 space-y-2">
          <Textarea data-testid="feedback-comment" value={comment} maxLength={1000}
            onChange={(e) => setComment(e.target.value)} placeholder="Anything you'd like to share? (optional)"
            className="min-h-[70px] rounded-xl bg-white text-sm" />
          <label className="flex items-center gap-2 text-xs text-[#475569]">
            <Checkbox data-testid="feedback-feature" checked={feature} onCheckedChange={(v) => setFeature(!!v)} />
            You may feature my feedback
          </label>
          <Button data-testid="feedback-submit" size="sm" disabled={saving} onClick={send}
            className="rounded-xl bg-navy hover:bg-navy-hover text-white">
            {saving ? <Loader2 size={15} className="mr-2 animate-spin" /> : null} Submit
          </Button>
        </div>
      )}
    </div>
  );
}

/** Inline, auto-gated prompt shown after a key action. Renders nothing if already
 *  submitted/dismissed for this context this session. */
export function FeedbackPrompt({ context }) {
  const { shouldShow, dismiss } = useFeedback();
  const [gone, setGone] = useState(false);
  if (gone || !shouldShow(context)) return null;
  return (
    <div className="mt-4 rounded-xl border border-[#E2E8F0] bg-white p-4 shadow-sm" data-testid={`feedback-prompt-${context}`}>
      <FeedbackCard context={context} onDone={() => setGone(true)} onDismiss={() => { dismiss(context); setGone(true); }} />
    </div>
  );
}

/** User-triggered general feedback modal (always available from the menu). */
export function FeedbackModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="feedback-modal">
        <DialogHeader><DialogTitle>Send feedback</DialogTitle></DialogHeader>
        <FeedbackCard context="general" onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
